import * as React from 'react';
import { Route, Switch, Link } from 'react-router-dom';
import { History } from 'history';
import { create, update, IDatabaseCreate, IDatabaseCreateField, get as getDatabase, IDatabase as IDatabaseType, format, IDatabaseField } from '../../../services/database';
import { updateSession as updateSavedSession, getSession } from '../../../services/session';
import { DATABASE_CREATE_PATH, DATABASE_UPDATE_PATH, DASHBOARD_PATH } from '../../../constants';

import General from './General';
import Fields from './Fields';
import Search from './Search';
import Layout from './Layout';
import Review from './Review';

import * as IDatabase from '../../../types/database';
import { IState as FieldsState } from './Fields';
import { IState as GeneralState } from './General';
import { IState as SearchState } from './Search';
import { IState as LayoutState } from './Layout';
import { FormattedMessage, InjectedIntlProps, injectIntl, defineMessages } from 'react-intl';
import defaultDatabaseFields from '../../../data/db-fields.js';
import * as databaseCategories from '../../../data/db-categories.js';

// Here we have default fields
// {
//      id: string, any random string that is unique
//      name: string, the name of the field
//      type: 'Short Text' | 'Single' | 'Multi' | 'Long Text', 'Multi' and 'Long Text' should be entered but their values are implied based on the predefined
//      options: string[], when predefined is true this is the list of predefined options that are availble to pick from
//      isRequired: boolean, if this field is required to submit an info page
// }

// TODO i18n
const defaultFields: IDatabase.ISection[] = defaultDatabaseFields;

export interface IDatabaseSetupSession {
    fields: FieldsState | null;
    general: GeneralState | null;
    search: SearchState | null;
    layout: LayoutState | null;
}

interface IProps {
    history: History;
    match?: {
        params: {
            databaseName: string
        }
    };
}

interface IState {
    hasLoadedPrevSession: boolean;
    session: IDatabaseSetupSession | null;
    routes: Array<{ name: string, id: string, path: string, component: ({ history }: { history: History }) => any }>;
}

class Index extends React.Component<IProps & InjectedIntlProps, IState> {

    messages = defineMessages({
        createError: {
            id: 'db.index.create.error',
            defaultMessage: 'There was an error creating your database. Please refresh and try again.'
        },
        setupError: {
            id: 'db.index.setup.error',
            defaultMessage: 'You have not finished all the required sections of your database. Please verify all previous steps have been completed.'
        },
        breadcrumbDash: {
            id: 'db.index.breadcrumb.dashboard',
            defaultMessage: 'Dashboard'
        },
        breadcrumbSetup: {
            id: 'db.index.breadcrumb.setup',
            defaultMessage: 'Setup New Database'
        },
        cancel: {
            id: 'db.index.setup.cancel',
            defaultMessage: 'Are you sure you want to cancel?'
        },
        clear: {
            id: 'db.index.setup.clear',
            defaultMessage: 'Are you sure you want to start over? This can not be undone.'
        },
        generalInfo: {
            id: 'db.index.nav.general',
            defaultMessage: 'General Info'
        },
        dbFields: {
            id: 'db.index.nav.fields',
            defaultMessage: 'Database Fields'
        },
        searchFilters: {
            id: 'db.index.nav.filters',
            defaultMessage: 'Search Filters'
        },
        infoPages: {
            id: 'db.index.nav.pages',
            defaultMessage: 'Info Page Settings'
        },
        review: {
            id: 'db.index.nav.review',
            defaultMessage: 'Review'
        },
        next: {
            id: 'db.index.btn.next',
            defaultMessage: 'Next'
        },
        save: {
            id: 'db.index.btn.save',
            defaultMessage: 'Save'
        }
    });

    constructor(props: IProps & InjectedIntlProps) {
        super(props);
        this.state = {
            session: null,
            hasLoadedPrevSession: false,
            routes: this.getRoutes(DATABASE_CREATE_PATH)
        };
    }

    getRoutes = (basePath: string) => [
        {
            name: this.props.intl.formatMessage(this.messages.generalInfo),
            id: 'general',
            path: `${basePath}/general`,
            component: ({ history }: { history: History }) => (
                <General
                    getSavedSession={this.getSavedSession}
                    history={history}
                    handleSave={this.handleStepSave}
                    handleCancel={this.handleCancel}
                    renderNextStepLink={this.renderNextStepLink}
                    handleClearSession={!(this.props.match && this.props.match.params && this.props.match.params.databaseName) && this.clearSession}
                />
            )
        },
        {
            name: this.props.intl.formatMessage(this.messages.dbFields),
            id: 'fields',
            path: `${basePath}/fields`,
            component: ({ history }: { history: History }) => (
                <Fields
                    getSavedSession={this.getSavedSession}
                    defaultFields={defaultFields}
                    history={history}
                    renderNextStepLink={this.renderNextStepLink}
                    handleSave={this.handleStepSave}
                />
            )
        },
        {
            name: this.props.intl.formatMessage(this.messages.searchFilters),
            id: 'search',
            path: `${basePath}/search`,
            component: ({ history }: { history: History }) => (
                <Search
                    getSavedSession={this.getSavedSession}
                    history={history}
                    handleSave={this.handleStepSave}
                    handleCancel={this.handleCancel}
                    renderNextStepLink={this.renderNextStepLink}
                />
            )
        },
        {
            name: this.props.intl.formatMessage(this.messages.infoPages),
            id: 'layout',
            path: `${basePath}/layout`,
            component: ({ history }: { history: History }) => (
                <Layout
                    history={history}
                    getSavedSession={this.getSavedSession}
                    handleSave={this.handleStepSave}
                    handleCancel={this.handleCancel}
                    renderNextStepLink={this.renderNextStepLink}
                />
            )
        },
        {
            name: this.props.intl.formatMessage(this.messages.review),
            id: `review`,
            path: `${basePath}/review`,
            component: ({ history }: { history: History }) => (
                <Review
                    history={history}
                    getSavedSession={this.getSavedSession}
                    handleCreate={this.handleCreate}
                    handleCancel={this.handleCancel}
                    routes={this.state.routes}
                />
            )
        }
    ];

    async componentDidMount() {
        if (this.props.match && this.props.match.params && this.props.match.params.databaseName) {
            const { databaseName } = this.props.match.params;
            this.setState({ routes: this.getRoutes(DATABASE_UPDATE_PATH(databaseName)) });
        }
    };

    handleCancel = () => {
        const r = confirm(this.props.intl.formatMessage(this.messages.cancel));
        if (r) this.props.history.push(DASHBOARD_PATH);
    };

    clearSession = async () => {
        const r = confirm(this.props.intl.formatMessage(this.messages.clear));
        if (r) {
            await updateSavedSession(null);
            window.location.href = `${DATABASE_CREATE_PATH}/general`
        }
    };

    getCurrentIndex = () => {
        const currentPath = window.location.pathname;
        return this.state.routes.findIndex(x => currentPath.indexOf(x.path) !== -1);
    };

    handleStepSave = async (state: any) => {
        window.onbeforeunload = null;
        const index = this.getCurrentIndex();
        const isEditng = !!(this.props.match && this.props.match.params && this.props.match.params.databaseName)
        if (isEditng && window.confirm("Are you sure you want to make changes to your database? These changes will be live immediately and will affect all your current pages.")) {
            if (index > -1) await this.updateState(this.state.routes[index].id, state);
            await this.handleCreate(false);
        } else if (!isEditng) {
            if (index > -1) await this.updateState(this.state.routes[index].id, state);
        } else {
            window.location.reload();
        }
    };

    handleCreate = async (isReview = true) => {
        if (
            this.state.session !== null &&
            this.state.session.general !== null &&
            this.state.session.fields !== null &&
            this.state.session.search !== null &&
            this.state.session.layout !== null
        ) {
            const { general, fields, search, layout } = this.state.session;
            const processedFields: IDatabaseCreateField[] = [];
            fields.selected.forEach(x => {
                x.fields.forEach(y => {
                    processedFields.push({
                        group: x.name,
                        name: y.name,
                        label: y.label,
                        type: y.type,
                        required: y.isRequired,
                        predefined: !!y.predefined,
                        selection: y.selection || 'n/a',
                        options: y.options,
                        extra: JSON.stringify({ sectionOrder: x.order, fieldOrder: y.order, help: (y.extra && y.extra.help) || '', type: (y.extra && y.extra.type) || '' })
                    });
                });
            });
            const data: IDatabaseCreate = {
                title: general.name,
                category: general.category,
                link: general.link || '',
                access: general.isPublic ? 'public' : 'private',
                fields: processedFields,
                filters: search.selected.map(x => format(x.group, x.name)),
                settings: {
                    layout: layout.layout,
                    allowAttachments: !!layout.allowAttachments,
                },
            };

            try {
                let result = { success: false };
                if (this.props.match && this.props.match.params.databaseName) {
                    const { databaseName } = this.props.match.params;
                    result = await update(databaseName, data);
                } else result = await create(data);
                if (result.success && isReview) {
                    await updateSavedSession(null);
                    this.props.history.push(DASHBOARD_PATH);
                }
            } catch (ex) {
                console.error(ex);
                alert(this.props.intl.formatMessage(this.messages.createError));
            }
        } else {
            alert(this.props.intl.formatMessage(this.messages.setupError));
        }
    };

    getSavedSession = async () => {
        if (this.props.match && this.props.match.params && this.props.match.params.databaseName && !this.state.hasLoadedPrevSession) {
            const { databaseName } = this.props.match.params;
            const databaseResult = await getDatabase(databaseName);
            if (databaseResult.success && databaseResult.database) {
                const database: IDatabaseType = databaseResult.database;
                const selected: IDatabase.ISection[] = [];
                const search: SearchState = {
                    selected: [],
                    options: []
                }
                const sections = defaultFields;
                const general: GeneralState = {
                    category: database.category,
                    categories: [...databaseCategories, database.category],
                    name: database.title,
                    link: database.link,
                    isPublic: database.access === 'public',
                    validDatabaseLink: null,
                    validCategory: null,
                    validTitle: null
                }
                database.fields = database.fields.map(field => ({ ...field, extra: field.extra ? JSON.parse(field.extra) : {} }))
                database.fields.forEach(field => {
                    const section = selected.find(x => x.name === field.group);
                    let defaultField: any = null;
                    const metaData: any = field.extra;
                    let defaultSection: any = sections.find(x => x.name === field.group);
                    if (defaultSection) {
                        defaultField = defaultSection.fields.find(x => x.name === field.name);
                        defaultSection = { ...defaultSection, order: metaData && metaData.sectionOrder }
                    }
                    else {
                        defaultSection = {
                            name: field.group,
                            id: sections.length + 1,
                            isDefault: false,
                            fields: [],
                            order: metaData && metaData.sectionOrder
                        }
                        sections.push(defaultSection)
                    }
                    if (section) {
                        const tempField = defaultField ? { ...defaultField, ...field } : {
                            ...field,
                            isRequired: field.required,
                            id: `${Math.random()}-${field.name}`,
                            order: metaData && metaData.fieldOrder
                        }
                        if (!defaultField)
                            defaultSection.fields.push(tempField)
                        section.fields.push(tempField)
                    }
                    else {
                        const tempField = defaultField ? { ...defaultField, ...field } :
                            {
                                ...field,
                                isRequired: field.required,
                                id: `${Math.random()} ${field.name}`,
                                order: metaData && metaData.fieldOrder
                            };
                        if (!defaultField)
                            defaultSection.fields.push(tempField)
                        selected.push({
                            ...defaultSection,
                            fields: [
                                tempField
                            ]
                        })
                    }
                })
                database.filters.forEach(filter => {
                    selected.forEach(s => {
                        s.fields.forEach(f => {
                            if (format(s.name, f.name) === filter) search.selected.push({ ...f, group: s.name })
                        })
                    })
                })
                const fields: FieldsState = {
                    edit: null,
                    currentSectionIndex: 0,
                    sections: [],
                    selected: selected.sort((a, b) => {
                        if (a.name < b.name) { return -1; }
                        if (a.name > b.name) { return 1; }
                        return 0;
                    })
                }
                const layout: LayoutState = {
                    selectedSections: fields.selected,
                    allowAttachments: database.settings.allowAttachments,
                    layout: database.settings.layout
                }

                const tempSession = {
                    fields,
                    general,
                    layout,
                    search
                };

                this.setState({
                    session: tempSession,
                    hasLoadedPrevSession: true,
                });
                return tempSession;
            }

        }
        // return null;
        else if (!this.state.hasLoadedPrevSession) {
            const session = await getSession();
            this.setState({
                session: session ? session.object : null,
                hasLoadedPrevSession: true,
            });
            return session ? session.object : null;
        }
        return this.state.session;
    };

    updateSession = async () => {
        return updateSavedSession(this.state.session)
    };

    updateState = async (name: string, state: any) => {
        return new Promise((resolve, reject) => {
            if (name) {
                /* tslint:disable */
                let session: IDatabaseSetupSession = { ...(this.state.session || { fields: null, general: null, search: null, layout: null }) };
                session[name] = state;
                this.setState({ session }, async () => {
                    await this.updateSession()
                    resolve(session)
                });
                /* tslint:enabled */
            } else {
                reject('INVALID SESSION')
            }
        })
    };

    renderNextStepLink = (onPress: () => boolean) => {
        const currentPath = window.location.pathname;
        const index = this.state.routes.findIndex(x => currentPath.indexOf(x.path) !== -1);
        const isEdit = !!(this.props.match && this.props.match.params && this.props.match.params.databaseName)
        // on edit, set history to return to last location; on new got to next setup step
        const targetRouteIndex = isEdit ? (this.state.routes.length - 1) : (index + 1);
        return (
            <button onClick={() => (onPress() !== false && this.props.history.push(this.state.routes[targetRouteIndex].path))} className="primary">
                {this.props.intl.formatMessage(isEdit ? this.messages.save : this.messages.next)}
            </button>
        );
    };

    renderSteps = () => {
        const currentPath = window.location.pathname;
        const index = this.state.routes.findIndex(x => currentPath.indexOf(x.path) !== -1);
        return this.state.routes.map((x, i) => {
            return <li key={`${x.name}-${i}`} className={i <= index ? 'active' : ''}><Link to={x.path}>{x.name}</Link></li>;
        });
    };

    render() {
        return (
            <section className="wrap d-flex flex-row flex-fill">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12 breadcrumb-col">
                            <ul>
                                <li key="bc1">
                                    <Link to={DASHBOARD_PATH}>{this.props.intl.formatMessage(this.messages.breadcrumbDash)}</Link>
                                </li>
                                <li key="bc2">
                                    <i className="fas fa-angle-right" />
                                </li>
                                <li key="bc3">{this.props.intl.formatMessage(this.messages.breadcrumbSetup)}</li>
                            </ul>
                        </div>
                        <div className="col-lg-12 pagetitle">
                            <h2><FormattedMessage id="db.index.new" defaultMessage="Setup New Database" /></h2>
                        </div>
                        <div className="stepList col-lg-12">
                            <ul className="stepListbar">{this.renderSteps()}</ul>
                        </div>
                        <div className="col-lg-12">
                            <Switch>
                                {this.state.routes.map((route, i) =>
                                    <Route
                                        key={route.path}
                                        exact
                                        path={route.path}
                                        component={route.component}
                                        routes={this.state.routes}
                                    />
                                )}
                            </Switch>
                        </div>
                    </div>
                </div>
            </section>
        );
    }
}
export default injectIntl(Index);


// WEBPACK FOOTER //
// ./src/client/database/setup/index.tsx